import React, { useEffect, useState, useMemo, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { TypeAnimation } from 'react-type-animation';
import { useInView } from 'react-intersection-observer';
import { 
  Zap, 
  Smartphone, 
  Code, 
  Headphones, 
  Palette, 
  Cloud, 
  Shield, 
  Clock,
  Mail,
  Phone,
  MapPin,
  Github,
  Linkedin,
  Twitter,
  ArrowRight,
  Menu
} from 'lucide-react';

// Componentes de animación
import FloatingParticles from './components/FloatingParticles';
import MagneticCard from './components/MagneticCard';
import ParallaxSection from './components/ParallaxSection';
import CustomCursor from './components/CustomCursor';
import ScrollProgress from './components/ScrollProgress';
import RevealOnScroll from './components/RevealOnScroll';
import BackToTop from './components/BackToTop';
import { usePerformance } from './hooks/usePerformance';

function App() {
  const { isLowPerformance, isReducedMotion } = usePerformance();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const { scrollY } = useScroll();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });
  const [animatedSections, setAnimatedSections] = useState(new Set());
  const parallaxRefs = useRef({});

  // Efecto de parallax para el fondo
  const backgroundY = useTransform(scrollY, [0, 1000], [0, 300]);

  // Optimizar animaciones para dispositivos lentos
  const animationConfig = useMemo(() => ({
    duration: isLowPerformance ? 0.5 : 0.8,
    type: isLowPerformance ? "tween" : "spring",
    stiffness: isLowPerformance ? 100 : 50,
    damping: isLowPerformance ? 20 : 15,
  }), [isLowPerformance]);

  // Función para manejar animaciones de entrada
  const handleIntersection = (sectionId) => {
    if (!animatedSections.has(sectionId)) {
      setAnimatedSections(prev => new Set([...prev, sectionId]));
    }
  };

  // Función para manejar parallax
  const handleParallax = (e) => {
    if (isLowPerformance) return;
    
    const scrolled = window.pageYOffset;
    Object.keys(parallaxRefs.current).forEach(key => {
      const element = parallaxRefs.current[key];
      if (element) {
        const speed = 0.5;
        const yPos = -(scrolled * speed);
        element.style.transform = `translateY(${yPos}px)`;
      }
    });
  };

  useEffect(() => {
    window.addEventListener('scroll', handleParallax);
    return () => window.removeEventListener('scroll', handleParallax);
  }, [isLowPerformance]);

  const services = [
    {
      icon: <Zap className="w-8 h-8 text-blue-400" />,
      title: "Automatización de procesos",
      description: "Optimiza tu flujo de trabajo con soluciones automatizadas personalizadas."
    },
    {
      icon: <Smartphone className="w-8 h-8 text-blue-400" />,
      title: "Aplicaciones web y móviles",
      description: "Desarrollo de aplicaciones modernas y responsivas para todas las plataformas."
    },
    {
      icon: <Code className="w-8 h-8 text-blue-400" />,
      title: "Integraciones personalizadas",
      description: "Conecta tus sistemas existentes con APIs y servicios de terceros."
    },
    {
      icon: <Headphones className="w-8 h-8 text-blue-400" />,
      title: "Soporte técnico continuo",
      description: "Mantenimiento y soporte 24/7 para garantizar el funcionamiento óptimo."
    }
  ];

  const advantages = [
    { icon: <Palette className="w-6 h-6 text-blue-400" />, text: "Diseño UI personalizado" },
    { icon: <Cloud className="w-6 h-6 text-blue-400" />, text: "Despliegue en la nube" },
    { icon: <Shield className="w-6 h-6 text-blue-400" />, text: "Seguridad de nivel empresarial" },
    { icon: <Clock className="w-6 h-6 text-blue-400" />, text: "Entrega rápida y confiable" }
  ];

  return (
    <div className="min-h-screen relative overflow-x-hidden modern-smooth-scroll">
      {/* Fondo espacial */}
      <div 
        className="fixed inset-0 z-0"
        style={{
          background: 'linear-gradient(rgba(10, 10, 10, 0.7), rgba(10, 10, 10, 0.8)), url(/space.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          backgroundRepeat: 'no-repeat'
        }}
      ></div>
      
      {/* Efectos de fondo */}
      <FloatingParticles />
      
      {/* Componentes de efectos de scroll */}
      {!isLowPerformance && !isReducedMotion && <CustomCursor />}
      <ScrollProgress />
      <BackToTop />
      
      {/* Header Dinámico y Profesional */}
      <motion.header 
        className="fixed top-0 w-full z-50 professional-header"
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ ...animationConfig, duration: 0.8 }}
      >
        {/* Fondo con gradiente dinámico */}
        <div className="header-bg-gradient"></div>
        
        {/* Navegación principal */}
        <div className="header-main">
          <div className="container mx-auto px-4 py-4">
            <motion.div 
              className="flex items-center justify-between"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...animationConfig, delay: 0.5 }}
            >
              {/* Logo con animación mejorada */}
              <motion.div 
                className="flex items-center space-x-3"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ ...animationConfig, delay: 0.6 }}
                whileHover={!isReducedMotion ? { scale: 1.05 } : {}}
              >
                <img 
                  src="/favicon.svg" 
                  alt="Data Saints Logo" 
                  className="w-10 h-10"
                />
                <div className="logo-text-header">
                  <span className="text-xl font-bold text-white">Data Saints</span>
                  <span className="text-xs text-blue-400 block -mt-1">Software Solutions</span>
                </div>
              </motion.div>

              {/* Navegación principal */}
              <nav className="hidden lg:flex items-center space-x-8">
                {[
                  { name: 'Inicio', href: '#inicio' },
                  { name: 'Servicios', href: '#servicios' },
                  { name: 'Ventajas', href: '#ventajas' },
                  { name: 'Contacto', href: '#contacto' }
                ].map((item, index) => (
                  <motion.a
                    key={item.name}
                    href={item.href}
                    className="nav-link-header"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ ...animationConfig, delay: 0.7 + index * 0.1 }}
                    whileHover={!isReducedMotion ? { y: -2 } : {}}
                  >
                    {item.name}
                  </motion.a>
                ))}
              </nav>

              {/* Botón CTA y menú móvil */}
              <motion.div 
                className="flex items-center space-x-4"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ ...animationConfig, delay: 0.8 }}
              >
                <motion.button 
                  className="cta-button-header"
                  whileHover={!isReducedMotion ? { scale: 1.05, y: -2 } : {}}
                  whileTap={!isReducedMotion ? { scale: 0.95 } : {}}
                >
                  <span>Consulta Gratuita</span>
                  <ArrowRight className="w-4 h-4 ml-2" />
                </motion.button>
                
                {/* Menú móvil */}
                <motion.button 
                  className="lg:hidden mobile-menu-button"
                  whileHover={!isReducedMotion ? { scale: 1.1 } : {}}
                  whileTap={!isReducedMotion ? { scale: 0.9 } : {}}
                >
                  <Menu className="w-6 h-6" />
                </motion.button>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Indicador de progreso */}
        <motion.div 
          className="header-progress-bar"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 2, delay: 1 }}
        />
      </motion.header>

      {/* Hero Section con Carrusel */}
      <section className="min-h-screen flex items-center justify-center relative pt-20 modern-parallax-section">
        <div 
          className="modern-parallax-bg"
          ref={el => parallaxRefs.current.hero = el}
        ></div>
        <div className="container mx-auto px-4 relative z-10">
          <ParallaxSection speed={0.3}>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ ...animationConfig, delay: 0.2 }}
              className="mb-8 text-center"
            >
              {/* Logo moderno */}
              <motion.div 
                className="relative inline-block mb-8 modern-cursor-glow"
                animate={!isReducedMotion ? { 
                  scale: [1, 1.05, 1]
                } : {}}
                transition={{ 
                  duration: isLowPerformance ? 6 : 4, 
                  repeat: Infinity, 
                  ease: "easeInOut" 
                }}
              >
                <div className="w-32 h-32 mx-auto relative">
                  <img 
                    src="/favicon.svg" 
                    alt="Data Saints Logo" 
                    className="w-full h-full object-contain"
                  />
                </div>
              </motion.div>
            </motion.div>
          </ParallaxSection>

          <ParallaxSection speed={0.5}>
            <motion.h1
              initial={{ opacity: 0, y: 50, scale: 0.5 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ ...animationConfig, delay: 0.4 }}
              className="text-6xl md:text-8xl font-bold modern-text-gradient-animated text-shadow-modern mb-6 text-center modern-text-reveal"
              whileHover={!isReducedMotion ? { scale: 1.02 } : {}}
            >
              DATA SAINTS
            </motion.h1>
          </ParallaxSection>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...animationConfig, delay: 0.6 }}
            className="text-xl md:text-2xl text-white/80 mb-12 text-center"
          >
            <TypeAnimation
              sequence={[
                'Soluciones de software a medida.',
                2000,
                'Desarrollo confiable, soluciones reales.',
                2000,
              ]}
              wrapper="span"
              speed={isLowPerformance ? 30 : 50}
              repeat={Infinity}
            />
          </motion.div>

          {/* Carrusel Principal con Parallax */}
          <ParallaxSection speed={0.8}>
            <motion.div
              initial={{ opacity: 0, y: 50, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ ...animationConfig, delay: 0.8 }}
              className="mb-12"
              style={{
                transform: `translateY(${useTransform(scrollY, [0, 1000], [0, -200]).get()}px)`
              }}
            >
              {/* The SimpleCarousel component was removed, so this section is now empty */}
            </motion.div>
          </ParallaxSection>

          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ ...animationConfig, delay: 1.0 }}
            className="text-center"
          >
            <motion.button 
              className="modern-button text-lg modern-cursor-glow"
              whileHover={!isReducedMotion ? { scale: 1.05, y: -2 } : {}}
              whileTap={!isReducedMotion ? { scale: 0.95 } : {}}
            >
              <span>Solicita una consulta gratuita</span>
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Servicios Section */}
      <RevealOnScroll direction="up" delay={0.2}>
        <section 
          id="servicios" 
          className="py-20 relative modern-parallax-section" 
          ref={ref}
          onMouseEnter={() => handleIntersection('servicios')}
        >
          <div 
            className="modern-parallax-bg"
            ref={el => parallaxRefs.current.servicios = el}
          ></div>
          <div className="container mx-auto px-4 relative z-10">
            <ParallaxSection speed={0.4}>
              <RevealOnScroll direction="down" delay={0.3}>
                <motion.h2
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={animationConfig}
                  className="text-4xl md:text-5xl font-bold text-center modern-text-gradient-animated mb-16 modern-text-reveal"
                >
                  Nuestros Servicios
                </motion.h2>
              </RevealOnScroll>
            </ParallaxSection>

            <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 modern-stagger-animation ${animatedSections.has('servicios') ? 'animated' : ''}`}>
              {services.map((service, index) => (
                <RevealOnScroll key={index} direction="up" delay={0.4 + index * 0.1}>
                  <ParallaxSection speed={0.2 + index * 0.1}>
                    <MagneticCard>
                      <motion.div
                        initial={{ opacity: 0, y: 50, rotateX: -90 }}
                        animate={inView ? { opacity: 1, y: 0, rotateX: 0 } : {}}
                        transition={{ ...animationConfig, delay: index * 0.1 }}
                        className="modern-card p-6 hover:bg-white/5 transition-all duration-300 group modern-hover-lift"
                      >
                        <motion.div 
                          className="mb-4 group-hover:scale-110 transition-transform duration-300"
                          animate={!isReducedMotion ? { rotate: [0, 10, -10, 0] } : {}}
                          transition={{ 
                            duration: isLowPerformance ? 3 : 2, 
                            repeat: Infinity, 
                            delay: index * 0.5 
                          }}
                        >
                          {service.icon}
                        </motion.div>
                        <h3 className="text-xl font-bold text-white mb-3">{service.title}</h3>
                        <p className="text-white/70 text-sm leading-relaxed">{service.description}</p>
                      </motion.div>
                    </MagneticCard>
                  </ParallaxSection>
                </RevealOnScroll>
              ))}
            </div>
          </div>
        </section>
      </RevealOnScroll>

      {/* Ventajas Section */}
      <section 
        id="ventajas" 
        className="py-20 relative bg-white/5 modern-parallax-section"
        onMouseEnter={() => handleIntersection('ventajas')}
      >
        <div 
          className="modern-parallax-bg"
          ref={el => parallaxRefs.current.ventajas = el}
        ></div>
        <div className="container mx-auto px-4 relative z-10">
          <ParallaxSection speed={0.6}>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={animationConfig}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-bold text-center modern-text-gradient-animated mb-16 modern-text-reveal"
            >
              ¿Por qué elegirnos?
            </motion.h2>
          </ParallaxSection>

          <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 modern-stagger-animation ${animatedSections.has('ventajas') ? 'animated' : ''}`}>
            {advantages.map((advantage, index) => (
              <ParallaxSection key={index} speed={0.3 + index * 0.1}>
                <MagneticCard>
                  <motion.div
                    initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50, scale: 0.8 }}
                    whileInView={{ opacity: 1, x: 0, scale: 1 }}
                    transition={{ ...animationConfig, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-center space-x-4 p-4 modern-border-gradient modern-hover-lift"
                    whileHover={!isReducedMotion ? { scale: 1.05 } : {}}
                  >
                    <motion.div 
                      className="flex-shrink-0"
                      animate={!isReducedMotion ? { rotate: [0, 360] } : {}}
                      transition={{ 
                        duration: isLowPerformance ? 5 : 3, 
                        repeat: Infinity, 
                        ease: "linear", 
                        delay: index * 0.5 
                      }}
                    >
                      {advantage.icon}
                    </motion.div>
                    <span className="text-white/80">{advantage.text}</span>
                  </motion.div>
                </MagneticCard>
              </ParallaxSection>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contacto" className="py-16 relative bg-white/5 border-t border-white/10 modern-parallax-section">
        <div 
          className="modern-parallax-bg"
          ref={el => parallaxRefs.current.footer = el}
        ></div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={animationConfig}
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 modern-stagger-animation animated"
          >
            {/* Contacto */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ ...animationConfig, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold modern-text-gradient-animated mb-6 modern-text-reveal">
                Contacto
              </h3>
              <div className="space-y-4">
                {[
                  { icon: <Mail className="w-5 h-5 text-blue-400" />, text: "info@datasaints.com" },
                  { icon: <Phone className="w-5 h-5 text-blue-400" />, text: "+1 (555) 123-4567" },
                  { icon: <MapPin className="w-5 h-5 text-blue-400" />, text: "Ciudad Cyberpunk, CP 2024" }
                ].map((item, index) => (
                  <motion.div 
                    key={index}
                    className="flex items-center space-x-3 footer-contact-item"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ ...animationConfig, delay: 0.2 + index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={!isReducedMotion ? { x: 5 } : {}}
                  >
                    <span>{item.icon}</span>
                    <span className="text-white/80">{item.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Enlaces rápidos */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ ...animationConfig, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold modern-text-gradient-animated mb-6 modern-text-reveal">
                Enlaces
              </h3>
              <div className="space-y-2">
                {['Servicios', 'Ventajas'].map((item, index) => (
                  <motion.a
                    key={item}
                    href={`#${item.toLowerCase()}`}
                    className="block text-white/80 hover:text-white transition-colors footer-link-item"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ ...animationConfig, delay: 0.3 + index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={!isReducedMotion ? { x: 5, scale: 1.02 } : {}}
                  >
                    {item}
                  </motion.a>
                ))}
              </div>
            </motion.div>

            {/* Redes sociales */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ ...animationConfig, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold modern-text-gradient-animated mb-6 modern-text-reveal">
                Síguenos
              </h3>
              <div className="flex space-x-4">
                {[Github, Linkedin, Twitter].map((Icon, index) => (
                  <motion.a
                    key={index}
                    href="#"
                    className="text-white/80 hover:text-white transition-colors footer-social-item"
                    whileHover={!isReducedMotion ? { scale: 1.2, rotate: 360 } : {}}
                    whileTap={!isReducedMotion ? { scale: 0.9 } : {}}
                    initial={{ opacity: 0, scale: 0 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ ...animationConfig, delay: 0.4 + index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Icon className="w-6 h-6" />
                  </motion.a>
                ))}
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ ...animationConfig, delay: 0.2 }}
            viewport={{ once: true }}
            className="mt-12 pt-8 border-t border-white/10 text-center"
          >
            <p className="text-white/60 text-sm">
              2025 DATA SAINTS. | Desarrollado con tecnología de vanguardia.
            </p>
          </motion.div>
        </div>
      </footer>
    </div>
  );
}

export default App;
